import React from "react";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { POS_PALETTE } from "../lib/posColors";

const Swatches = ({ testId, value, auto, autoLabel, colors, onPick }) => (
    <div className="flex gap-1.5 mt-1.5 flex-wrap items-center">
        {auto && (
            <button data-testid={`${testId}-auto`} onClick={() => onPick(null)}
                className={`text-[10px] font-mono px-2 h-6 rounded border ${!value ? "border-blue-400 text-blue-300" : "border-[#1e293b] text-slate-400"}`}>{autoLabel}</button>
        )}
        {colors.map((c) => (
            <button key={c} data-testid={`${testId}-${c}`} onClick={() => onPick(c)} style={{ backgroundColor: c }}
                className={`w-5 h-5 rounded-full border-2 hover:scale-110 transition-transform ${(value || "").toLowerCase() === c ? "border-white" : "border-white/30"}`} />
        ))}
        <input data-testid={`${testId}-custom`} type="color" value={value || "#2563eb"} onChange={(e) => onPick(e.target.value)}
            className="w-6 h-6 rounded cursor-pointer bg-transparent border border-[#1e293b] p-0" />
    </div>
);

const Pct = ({ testId, label, value, onChange }) => (
    <div>
        <Label className="text-sm text-slate-200">{label}: {Math.round(value * 100)}%</Label>
        <Slider data-testid={testId} value={[value]} min={0} max={1} step={0.01} onValueChange={(v) => onChange(v[0])} className="mt-2" />
    </div>
);

// Fill / border / text colour, border width and transparency controls for position bubbles.
export function BubbleColorControls({ cur, applyStyle, t, showFill = true }) {
    const bOp = cur("borderOpacity") ?? (cur("noFill") || cur("borderColor") ? 1 : 0.8);
    return (
        <div data-testid="bubble-color-controls" className="space-y-3">
            {showFill && (
            <div>
                <Label className="text-sm text-slate-200">{t("pv_bubble_color")}</Label>
                <Swatches testId="bubble-bg" value={cur("bg")} auto autoLabel={t("pv_by_group")} colors={POS_PALETTE.slice(0, 8)} onPick={(c) => applyStyle("bg", c)} />
            </div>
            )}
            <Pct testId="bubble-bg-opacity" label={t("pv_bg_opacity")} value={cur("bgOpacity") ?? 1} onChange={(v) => applyStyle("bgOpacity", v)} />
            <div>
                <Label className="text-sm text-slate-200">{t("pv_border_color")}</Label>
                <Swatches testId="bubble-border" value={cur("borderColor")} auto autoLabel={t("pv_auto")} colors={["#ffffff", "#0b0f17", ...POS_PALETTE.slice(0, 5)]} onPick={(c) => applyStyle("borderColor", c)} />
            </div>
            <Pct testId="bubble-border-opacity" label={t("pv_border_opacity")} value={bOp} onChange={(v) => applyStyle("borderOpacity", v)} />
            <div>
                <Label className="text-sm text-slate-200">{t("pv_border_width")}: {cur("borderWidth") ?? 2}</Label>
                <Slider data-testid="bubble-border-width" value={[cur("borderWidth") ?? 2]} min={0} max={8} step={0.5}
                    onValueChange={(v) => applyStyle("borderWidth", v[0])} className="mt-2" />
            </div>
            <div>
                <Label className="text-sm text-slate-200">{t("pv_text_color")}</Label>
                <Swatches testId="text-color" value={cur("textColor") || "#ffffff"} colors={["#ffffff", "#0b0f17", "#2563eb", "#16a34a", "#f97316", "#dc2626"]} onPick={(c) => applyStyle("textColor", c)} />
            </div>
        </div>
    );
}

// Scope selector: only selected bubble / all bubbles of the same position / all bubbles.
export function BubbleScope({ scope, setScope, posLabel, hasSel, t }) {
    const opts = [["selected", t("pv_scope_selected")], ["position", t("pv_scope_position", { p: posLabel || "P" })], ["all", t("pv_scope_all")]];
    return (
        <div>
            <Label className="text-xs text-slate-400 font-mono">{t("pv_scope")}</Label>
            <div className="grid grid-cols-3 gap-1 mt-1">
                {opts.map(([k, l]) => (
                    <button key={k} data-testid={`bubble-scope-${k}`} disabled={k !== "all" && !hasSel} onClick={() => setScope(k)}
                        className={`text-[11px] leading-tight px-1.5 py-1.5 rounded border transition-colors disabled:opacity-40 ${scope === k ? "bg-blue-600 border-blue-500 text-white" : "border-[#1e293b] text-slate-300 hover:bg-[#182232]"}`}>{l}</button>
                ))}
            </div>
        </div>
    );
}
